import React, { useCallback, useMemo } from 'react';

import cn from 'classnames';
import _ from 'lodash';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';

import { RosterScout } from '@modules/advancement/duck/types';
import { groupYouthsAdultsParents } from '@modules/shared/components/PersonSelector/utils';
import { personNameBuilder } from '@modules/utils';
import { Button, Col, PersonAvatar, Row, Select } from '@shared/components';
import { userDataSel, userIdSel } from '@user';

import { selectPerson } from '../../duck';
import styles from './AddPersonButton.less';

const { Option } = Select;

const filterOption = (input: string, option: React.ReactElement) => {
  if (!option) return false;

  if (option.props.value === 'Label') {
    return true;
  }

  return option.props.title.toLowerCase().includes(input.toLowerCase());
};

type Options = Pick<
  RosterScout,
  | 'userId'
  | 'personFullName'
  | 'firstName'
  | 'lastName'
  | 'nickName'
  | 'isAdult'
  | 'positions'
  | 'gender'
>[];

type Props = {
  className?: string;
  persons: RosterScout[];
  selectedPersons: number[];
  notAllowedToRemoveIds?: number[];
  canRemove: boolean;
  onToggleView?: (isVisible: boolean) => void;
  onSelectPerson?: (userId: number) => void;
  onDeselectPerson: (userId: number) => void;
  label?: React.ReactNode;
  extraFilter?: (scout: RosterScout) => boolean;
  selectedUnitsHasDen?: boolean;
};

const AddPersonButton: React.FC<Props> = ({
  className,
  persons,
  selectedPersons,
  notAllowedToRemoveIds,
  onDeselectPerson,
  onSelectPerson,
  children,
  canRemove = false,
  extraFilter,
  label,
  selectedUnitsHasDen,
}) => {
  const componentId = `person-select-${uuidv4()}`;
  const dispatch = useDispatch();
  const loggedInUserId = useSelector(userIdSel);
  const loggedInUserData = useSelector(userDataSel);

  const foundLoggedInUser = useMemo(
    () => persons.find(person => person.userId === loggedInUserId),
    [persons, loggedInUserId],
  );

  const renderOption = (personsList: Options) =>
    _.orderBy(personsList, ['lastName', 'firstName']).map(person => {
      const { userId, personFullName, isAdult, gender } = person;

      return (
        <Option key={userId} value={userId} title={personFullName}>
          <Row type="flex" align="middle" className={styles.person}>
            <Col>
              <PersonAvatar
                className={styles.avatar}
                isAdult={isAdult}
                gender={gender}
              />
            </Col>
            <Col className={styles.personName}>
              {personNameBuilder.short(person)}
            </Col>
          </Row>
        </Option>
      );
    });

  const handleSelectPerson = useCallback(
    (userId: number) => {
      if (onSelectPerson) {
        onSelectPerson(userId);
      } else {
        dispatch(selectPerson(userId));
      }
    },
    [dispatch, onSelectPerson],
  );

  const handleDeselectPerson = useCallback(
    userId => {
      if (canRemove && !notAllowedToRemoveIds?.includes(userId)) {
        onDeselectPerson(userId);
      }
    },
    [canRemove, onDeselectPerson, notAllowedToRemoveIds],
  );

  const filterPerson = useCallback(
    (person: RosterScout) => {
      const { userId, memberId } = person;
      if (!userId || !memberId) return false;
      if (notAllowedToRemoveIds?.includes(userId)) return false;
      if (extraFilter) {
        return extraFilter(person);
      }
      return true;
    },
    [extraFilter, notAllowedToRemoveIds],
  );

  const options = useMemo<Options>(() => {
    const allowedPersons = persons
      .filter(filterPerson)
      .map(
        ({
          userId,
          personFullName,
          firstName,
          lastName,
          nickName,
          isAdult,
          positions,
          gender,
        }) => ({
          userId,
          personFullName,
          firstName,
          lastName,
          nickName,
          isAdult,
          positions,
          gender,
        }),
      );

    if (!foundLoggedInUser && loggedInUserData) {
      const {
        fullName: personFullName,
        firstName,
        lastName,
        nickName,
        positions,
        gender,
      } = loggedInUserData.profile;

      const loggedInPerson = {
        userId: loggedInUserId,
        personFullName,
        firstName,
        lastName,
        nickName,
        isAdult: true,
        positions,
        gender,
      };
      if (filterPerson(loggedInPerson as RosterScout)) {
        allowedPersons.push(loggedInPerson);
      }
    }

    return allowedPersons.sort((a, b) =>
      String(a.lastName).localeCompare(b.lastName),
    );
  }, [
    persons,
    foundLoggedInUser,
    loggedInUserData,
    loggedInUserId,
    filterPerson,
  ]);

  const focusOnSelect = useCallback(() => {
    const select = document.getElementById(componentId);
    select?.click();
  }, [componentId]);

  const toggleButton = useMemo(() => {
    if (React.isValidElement(children)) {
      return React.cloneElement(
        children as React.ReactElement<{ onClick: () => void }>,
        { onClick: focusOnSelect },
      );
    }

    return (
      <Button
        shape="round"
        type="primary"
        onClick={focusOnSelect}
        className={styles.addPersonButton}
        block
      >
        {label || <FormattedMessage id="progress.AddPersonButton.title" />}
      </Button>
    );
  }, [children, focusOnSelect, label]);

  const selectOptions = useMemo(() => {
    const formatedOptions = [];
    const updatedList = groupYouthsAdultsParents(options, selectedUnitsHasDen);
    const youths = renderOption(updatedList.youths);
    const adults = renderOption(updatedList.adults);
    const denChiefs = renderOption(updatedList.denChiefs);

    if (!_.isEmpty(youths)) {
      formatedOptions.push(
        <Option
          className={styles.default}
          disabled
          key={'labelYouths'}
          title="Youths"
          value="Label"
        >
          <strong>
            <FormattedMessage id="progress.dropdown.label.youths" />
          </strong>
        </Option>,
        ...youths,
      );
    }

    if (!_.isEmpty(adults)) {
      formatedOptions.push(
        <Option
          className={styles.default}
          disabled
          key={'labelAdults'}
          title="Leaders"
          value="Label"
        >
          <strong>
            <FormattedMessage id="progress.dropdown.label.adults" />
          </strong>
        </Option>,
        ...adults,
      );
    }

    if (!_.isEmpty(denChiefs)) {
      formatedOptions.push(
        <Option
          className={styles.default}
          disabled
          key={'labelDenChiefs'}
          title="DenChiefs"
          value="Label"
        >
          <strong>
            <FormattedMessage id="progress.dropdown.label.denChiefs" />
          </strong>
        </Option>,
        ...denChiefs,
      );
    }

    return formatedOptions;
  }, [options, selectedUnitsHasDen]);

  return (
    <div
      className={className ? cn(styles.container, className) : styles.container}
    >
      <Select
        className={styles.dropDownContainer}
        filterOption={filterOption}
        id={componentId}
        mode="multiple"
        autoClearSearchValue={false}
        onDeselect={handleDeselectPerson}
        onSelect={handleSelectPerson}
        optionFilterProp="title"
        showSearch
        value={selectedPersons}
      >
        {selectOptions}
      </Select>
      <div className={styles.buttonContainer}>{toggleButton}</div>
    </div>
  );
};

export default AddPersonButton;
